import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { fetchContentsForMapping } from "../../../services/contentsService";
import { getContentCategoriesList } from "../../../services/contentCategoriesService";
import { getContentTopicsList } from "../../../services/contentTopicsService";
import { paginateData, calculateTotalPages } from "../../../assets/js/script";
import EntriesDropdown from "../../Common/TableComponent/EntriesDropdown";
import { Pagination } from "../../Common/TableComponent/Pagination";
import { Loading } from "../../Common/OtherElements/Loading";
import { handleErrors } from "../../../utils/errorHandler";
import allImages from "../../../assets/images-import";
import {
  getContentId,
  getContentTitle,
  getCategoryName,
  getTopicName,
  getImageUrl,
  getPublishedOn,
  getMappedContentId,
} from "./sectionMappingUtils";

const mapDropdownOptions = (list, nameKey, pascalNameKey) =>
  (list || []).map((item) => ({
    value: String(item.id ?? item.Id ?? ""),
    label: item[nameKey] || item[pascalNameKey] || "-",
  }));

const AddContentModal = ({
  show,
  onHide,
  mappedItems,
  onBulkAdd,
  isSubmitting,
  canAdd,
}) => {
  const [allContents, setAllContents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [topicFilter, setTopicFilter] = useState("");
  const [searchTitle, setSearchTitle] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [entriesPerPage, setEntriesPerPage] = useState(10);

  const handleEntriesChange = (value) => {
    setEntriesPerPage(value);
    setCurrentPage(1);
  };
  const [currentPage, setCurrentPage] = useState(1);

  const mappedContentIdSet = useMemo(() => {
    const ids = new Set();
    (mappedItems || []).forEach((item) => {
      const cid = getMappedContentId(item);
      if (cid) ids.add(String(cid));
    });
    return ids;
  }, [mappedItems]);

  useEffect(() => {
    if (!show) {
      setSelectedIds([]);
      setCategoryFilter("");
      setTopicFilter("");
      setSearchTitle("");
      setCurrentPage(1);
      return;
    }

    const loadData = async () => {
      setLoading(true);
      try {
        const [contentsRes, catRes, topicRes] = await Promise.all([
          fetchContentsForMapping(),
          getContentCategoriesList(),
          getContentTopicsList(),
        ]);
        const list = contentsRes.data?.result || contentsRes.data || [];
        setAllContents(Array.isArray(list) ? list : []);
        const catList = catRes.data?.result || catRes.data || [];
        const topicList = topicRes.data?.result || topicRes.data || [];
        setCategories(
          mapDropdownOptions(
            Array.isArray(catList) ? catList : [],
            "categoryName",
            "CategoryName"
          )
        );
        setTopics(
          mapDropdownOptions(
            Array.isArray(topicList) ? topicList : [],
            "topicName",
            "TopicName"
          )
        );
      } catch (error) {
        handleErrors(error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [show]);

  const availableContents = useMemo(() => {
    return allContents.filter((item) => {
      const contentId = String(getContentId(item));
      if (mappedContentIdSet.has(contentId)) return false;

      const categoryId = String(
        item.contentCategoryId ?? item.ContentCategoryId ?? ""
      );
      const topicId = String(item.contentTopicId ?? item.ContentTopicId ?? "");
      const title = getContentTitle(item).toLowerCase();

      if (categoryFilter && categoryId !== categoryFilter) return false;
      if (topicFilter && topicId !== topicFilter) return false;
      if (
        searchTitle &&
        !title.includes(searchTitle.trim().toLowerCase())
      ) {
        return false;
      }
      return true;
    });
  }, [
    allContents,
    mappedContentIdSet,
    categoryFilter,
    topicFilter,
    searchTitle,
  ]);

  const currentData = paginateData(
    availableContents,
    currentPage,
    entriesPerPage
  );
  const totalPages = calculateTotalPages(
    availableContents.length,
    entriesPerPage
  );

  const toggleSelect = (contentId) => {
    const idStr = String(contentId);
    setSelectedIds((prev) =>
      prev.includes(idStr)
        ? prev.filter((x) => x !== idStr)
        : [...prev, idStr]
    );
  };

  const toggleSelectAllOnPage = () => {
    const pageIds = currentData.map((item) => String(getContentId(item)));
    const allSelected = pageIds.every((id) => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...pageIds])]);
    }
  };

  const handleAddSelected = () => {
    if (selectedIds.length === 0) return;
    onBulkAdd(selectedIds.map((id) => Number(id)));
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Add Content to Section</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row g-3 mb-3">
          <div className="col-md-4">
            <label className="form-label">Category</label>
            <select
              className="form-select"
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All categories</option>
              {categories.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label">Topic</label>
            <select
              className="form-select"
              value={topicFilter}
              onChange={(e) => {
                setTopicFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All topics</option>
              {topics.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label">Search Title</label>
            <input
              type="text"
              className="form-control"
              placeholder="Search by title..."
              value={searchTitle}
              onChange={(e) => {
                setSearchTitle(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>

        <div className="d-flex justify-content-between align-items-center mb-3">
          <EntriesDropdown
            entriesPerPage={entriesPerPage}
            onEntriesChange={(value) => {
              setEntriesPerPage(value);
              setCurrentPage(1);
            }}
          />
          <span className="text-muted small">
            {selectedIds.length} selected · {availableContents.length}{" "}
            available
          </span>
        </div>

        {loading ? (
          <Loading />
        ) : (
          <div className="table-responsive">
            <table className="table align-middle table-bordered">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      onChange={toggleSelectAllOnPage}
                      checked={
                        currentData.length > 0 &&
                        currentData.every((item) =>
                          selectedIds.includes(String(getContentId(item)))
                        )
                      }
                    />
                  </th>
                  <th>Image</th>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Topic</th>
                  <th>Published</th>
                </tr>
              </thead>
              <tbody>
                {currentData.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center text-muted py-4">
                      No contents available to add (already mapped or no
                      matches).
                    </td>
                  </tr>
                ) : (
                  currentData.map((item) => {
                    const contentId = getContentId(item);
                    const checked = selectedIds.includes(String(contentId));
                    return (
                      <tr key={contentId}>
                        <td>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={checked}
                            onChange={() => toggleSelect(contentId)}
                          />
                        </td>
                        <td>
                          <img
                            src={getImageUrl(item) || allImages.DefultImage}
                            alt=""
                            className="rounded"
                            style={{
                              width: 40,
                              height: 40,
                              objectFit: "cover",
                            }}
                            onError={(e) => {
                              e.target.src = allImages.DefultImage;
                            }}
                          />
                        </td>
                        <td>{getContentTitle(item)}</td>
                        <td>{getCategoryName(item)}</td>
                        <td>{getTopicName(item)}</td>
                        <td>{getPublishedOn(item)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalEntries={availableContents.length}
          entriesPerPage={entriesPerPage}
          onEntriesChange={handleEntriesChange}
          onPageChange={setCurrentPage}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onHide} disabled={isSubmitting}>
          Cancel
        </Button>
        {canAdd ? (
          <Button
            variant="secondary"
            onClick={handleAddSelected}
            disabled={isSubmitting || selectedIds.length === 0}
          >
            {isSubmitting
              ? "Adding..."
              : `Add Selected (${selectedIds.length})`}
          </Button>
        ) : null}
      </Modal.Footer>
    </Modal>
  );
};

export default AddContentModal;
